/**
 * MangaCommunityBanner — Dashboard banner that nudges athletes to contribute manga
 * for drills that still lack AI panels. Auto-dismisses for 24h when closed.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, X, ArrowRight, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISMISS_KEY = "manga_banner_dismissed_until";

const MangaCommunityBanner = () => {
  const navigate = useNavigate();
  const [needs, setNeeds] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [hidden, setHidden] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedUntil > Date.now()) {
      setHidden(true);
      return;
    }
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [needsRes, ldrRes] = await Promise.all([
          axios.get(`${API}/community/manga/needs`, { headers }),
          axios.get(`${API}/community/manga/leaderboard?limit=3`, { headers }),
        ]);
        setNeeds(needsRes.data);
        setLeaders(ldrRes.data?.leaders || []);
      } catch {
        /* silent */
      }
    };
    load();
  }, []);

  if (hidden || !needs || needs.count === 0) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setHidden(true);
  };

  const firstFew = (needs.drills || []).slice(0, 3).map((d) => d.name);

  return (
    <div
      className="relative bg-gradient-to-r from-orange-500/15 via-amber-500/10 to-zinc-900/10 border border-orange-500/30 p-4 rounded-sm"
      data-testid="manga-community-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors p-1"
        aria-label="Dismiss"
        data-testid="dismiss-manga-banner-btn"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="hidden sm:flex w-10 h-10 rounded-sm bg-orange-500/20 border border-orange-500/40 items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-orange-400 mb-1">
            Community Challenge
          </p>
          <p className="text-sm text-white font-bold leading-snug">
            <span className="text-orange-400" data-testid="manga-needs-count">{needs.count}</span>{" "}
            drill{needs.count === 1 ? "" : "s"} still need manga — be the first to draw one
          </p>
          {firstFew.length > 0 && (
            <p className="text-xs text-zinc-400 mt-1 truncate">
              Like: {firstFew.join(" · ")}
              {needs.count > firstFew.length && ` +${needs.count - firstFew.length} more`}
            </p>
          )}

          {/* Top contributors */}
          {leaders.length > 0 && (
            <div className="flex items-center gap-3 mt-2.5 flex-wrap">
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-400">
                <Trophy className="w-3 h-3" />
                Top Artists
              </div>
              {leaders.slice(0, 3).map((l, i) => (
                <span key={l.user_id} className="text-[10px] font-mono text-zinc-300">
                  <span className="text-amber-400 font-bold mr-1">#{i + 1}</span>
                  {l.name}
                  <span className="text-zinc-500 ml-1">· {l.panels_contributed}</span>
                </span>
              ))}
            </div>
          )}

          <button
            onClick={() => navigate("/engine?focus=manga")}
            className="mt-3 inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 transition-colors"
            data-testid="explore-manga-needs-btn"
          >
            Start drawing <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MangaCommunityBanner;
